import Rox, { RoxFetcherResult } from "rox-browser";
import { Config } from "@/shared/config";
import * as env from "@/shared/env";

const flagsContainer = {
  showTdaInvestmentLink: new Rox.Flag(true),
  overrideHealthPlanIsLinked: new Rox.Flag(false),
  showFundingMethod: new Rox.Flag(false),
  bend15691OrderByRelevance: new Rox.Flag(false),
  showBulkEnrollment: new Rox.Flag(false),
  drivewealthAllowManagedSignup: new Rox.Flag(false),
  drivewealthAllowSelfSignup: new Rox.Flag(false),
  showAllegacyInvestmentLink: new Rox.Flag(false),
  pauseOnboardingForUnclaimedSelfReportedEmployees: new Rox.Flag(false),
  allowDrivewealthPreenrollment: new Rox.Flag(false),
  lobAddressValidationIsEnabled: new Rox.Flag(false),
  showDynamicAllegacyFees: new Rox.Flag(false),
  bend15067EnableAdvisorAgreementsByType: new Rox.Flag(false),
  show2023UmbInterestRates: new Rox.Flag(false),
  bend15244DisplayDrivewealthInvestmentForHSABUsers: new Rox.Flag(false),
  hsa4475allowRolePermissions: new Rox.Flag(false),
  bend16432HsabExpenseManagement: new Rox.Flag(false),
  bend15789NotionalTransactions: new Rox.Flag(false),
  bend15878DebitCardManagementUi: new Rox.Flag(false),
  bend16025ShowHsabAccountCardChart: new Rox.Flag(false),
  bend16364HsabResourceNavigation: new Rox.Flag(false),
  bend16362ResourceFormsInEmployer: new Rox.Flag(false),
  bend16780CombinedAccountSummariesEnabled: new Rox.Flag(false),
  bend16311ResourceFormsInEmployee: new Rox.Flag(false),
  bend16322EnableHsabTransactions: new Rox.Flag(false),
  bend16400HsabNotionalFileAClaim: new Rox.Flag(false),
  bend16612ContributionHandling: new Rox.Flag(false),
  bend17151ContributionWidgetUpdate: new Rox.Flag(false),
  bend17383EmployerSsoUserSync: new Rox.Flag(false),
  bend18282ClaimSsoLinkWithParam: new Rox.Flag(false),
  bend18282ClaimSsoLinkDashboard: new Rox.Flag(false),
  bend18604NotionalAccountSummaries: new Rox.Flag(false),
  bend18927CreateExpenseFromNavbar: new Rox.Flag(false),
  hsa5812ConsumerLiveChat: new Rox.Flag(false),
  bend17254EnableProfileConsumerFields: new Rox.Flag(false),
  bend18761AddDeepLinkToResourcesPages: new Rox.Flag(false),
  bend18843MobileAppMinimumVersionRequired: new Rox.Flag(false),
  bend19778ViewExpenseButtonInTransactions: new Rox.Flag(false),
  bend19788FormDocumentLinksEnabled: new Rox.Flag(false),
  bend20018ActionNeededWidget: new Rox.Flag(false),
  bend20105ExpenseFilters: new Rox.Flag(false),
  bend17261EnableProfileAddEdit: new Rox.Flag(false),
  bend19874ExpensePayeeAddressLob: new Rox.Flag(false),
  bend20033DisplaySmartCommuteDeepLink: new Rox.Flag(false),
  hsa6356EnableHsabAdvisorAlerts: new Rox.Flag(false),
  bend19525ConsumerClaimsExchangeSettings: new Rox.Flag(false),
  bend20542RemoveSummariesAndTaxFormDeepLinks: new Rox.Flag(false),
  bend20636WexDebitCardUi: new Rox.Flag(false),
  bend20898MileageToggle: new Rox.Flag(false),
  bend20914DeepLinkForManageHealthcareClaims: new Rox.Flag(false),
  bend19850ProfileCoverageLevelUpdates: new Rox.Flag(false),
  bend21323UmbShowStatementPreferencesLink: new Rox.Flag(false),
  bend17254ProfileLobAddressValidation: new Rox.Flag(false)
};

type FeatureFlagContainer = typeof flagsContainer;

const alwaysDefaultInProdContainer = {
  UseDemoDataInjectionTableForDrivewealthMocks: new Rox.Flag(false)
};

type AlwaysDefaultInProdContainer = typeof alwaysDefaultInProdContainer;

type PortalFeatureFlags = {
  [Property in keyof FeatureFlagContainer]: boolean;
};

type AlwaysDefaultInProdFlags = {
  [Property in keyof AlwaysDefaultInProdContainer]: boolean;
};

export type FeatureFlags = PortalFeatureFlags & {
  alwaysDefaultInProd: AlwaysDefaultInProdFlags;
};

function flattenFlagsToTheirValues<T> (flagCollection: {[index: string]: Rox.Flag}) {
  return Object.keys(flagCollection).reduce(
    (obj, key) => {
      obj[key] = flagCollection[key].isEnabled();
      return obj;
    },
    {} as T
  );
}

function flattenFeatureFlags (): FeatureFlags {
  const alwaysDefaultInProd = flattenFlagsToTheirValues<AlwaysDefaultInProdFlags>(alwaysDefaultInProdContainer);
  return {
    ...flattenFlagsToTheirValues<PortalFeatureFlags>(flagsContainer),
    alwaysDefaultInProd
  };
}

export const defaultFlagState = flattenFeatureFlags();

export interface FeatureFlagCallbacks {
  onRetrieval: (flags: FeatureFlags) => void;
  onTargetingUpdate: (flags: FeatureFlags, userTargeting: UserTargetingUpdate) => void;
  onTimeout: () => void;
}

const callbackObjects: FeatureFlagCallbacks[] = [];

export function registerCallbacks (callbacks: FeatureFlagCallbacks) {
  callbackObjects.push(callbacks);
}

const onFlagRetrieval = () => {
  Rox.unfreeze();
  const featureFlags = flattenFeatureFlags();
  callbackObjects.forEach(obj => {
    obj.onRetrieval(featureFlags);
  });
};

const onTargetingUpdate = (targeting: UserTargetingUpdate) => {
  Rox.unfreeze();
  const featureFlags = flattenFeatureFlags();
  callbackObjects.forEach(obj => {
    obj.onTargetingUpdate(featureFlags, targeting);
  });
};

const onTimeout = () => {
  callbackObjects.forEach(obj => {
    obj.onTimeout();
  });
};

// tslint:disable-next-line:max-line-length
// documented here: https://docs.cloudbees.com/docs/cloudbees-feature-management-api/4.9/api-reference/javascript-browser-api#_configurationfetchedhandler
const configurationFetchedHandler = (arg: RoxFetcherResult) => {
  if (arg.hasChanges || arg.fetcherStatus === "APPLIED_FROM_NETWORK") {
    onFlagRetrieval();
  }
};

const initialize = () => {
  Rox.register("Portal", flagsContainer);
  if (!env.isProd()) {
    Rox.register("AlwaysDefaultInProd", alwaysDefaultInProdContainer);
  }

  Rox.setup(
    Config.RolloutKey,
    {
      configurationFetchedHandler
    });

  setTimeout(onTimeout, 3 * 1000);
};

export interface UserTargetingUpdate {
  email?: string;
  partnerId?: number;
  custodian?: string;
  employerId?: number;
}

export function updateTargeting (update: UserTargetingUpdate) {
  Object.keys(update).forEach(key => {
    const value = update[key];
    if (typeof value === "string") {
      Rox.setCustomStringProperty(key, value);
    } else if (typeof value === "number") {
      Rox.setCustomNumberProperty(key, value);
    } else if (typeof value === "boolean") {
      Rox.setCustomBooleanProperty(key, value);
    }
  });

  onTargetingUpdate(update);
}

export default {
  initialize,
  flags: flagsContainer
};